import React from 'react';
import { useParams } from 'react-router-dom';

import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Group, Modal, ScrollArea, SimpleGrid, Space, Stack, Text, Title } from '@mantine/core';

import { downloadFile } from './downloadUtils';
import { LoadingContainer } from '../common/LoadingContainer';
import type { ClonotypeAnalysisResponse, ClonotypingMethod } from '../generated/generatedOpenAPI';
import { useEnsureSelectedClonotypeDefinition } from '../hooks/useEnsureSelectedClonotypeDefinition';
import { useStartClonotypeAnalysis } from '../store/store';
import { getClonotypeEnumFromDefinition } from '../utils';

function HighlighText({ text, coloredIndexes }: { text: string; coloredIndexes: Array<[[number, number], 'none' | 'cdr1' | 'cdr2' | 'cdr3']> }) {
  const parts = React.useMemo(() => {
    return coloredIndexes.map(([range, type], index) => {
      const [start, end] = range;
      const color = type === 'cdr1' ? 'blue.3' : type === 'cdr2' ? 'blue.5' : type === 'cdr3' ? 'blue.7' : undefined;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <Text key={index} c={color} ff="monospace" size="sm">
          {text.slice(start, end)}
        </Text>
      );
    });
  }, [coloredIndexes, text]);

  return parts;
}

interface MultiAlignmentsProps {
  clonotypeId: number;
  barcodes: string[];
  onClose: () => void;
}

export function MultiAlignmentsModal({ clonotypeId, barcodes, onClose }: MultiAlignmentsProps) {
  const [data, setData] = React.useState<ClonotypeAnalysisResponse | null>(null);
  const [isLoadingVhAaDownload, setIsLoadingVhAaDownload] = React.useState(false);
  const [isLoadingVlAaDownload, setIsLoadingVlAaDownload] = React.useState(false);
  const [isLoadingVhDnaDownload, setIsLoadingVhDnaDownload] = React.useState(false);
  const [isLoadingVlDnaDownload, setIsLoadingVlDnaDownload] = React.useState(false);

  const { id: experimentId } = useParams<{ id: string }>();

  const clonotypeDefinition = useEnsureSelectedClonotypeDefinition();
  const clonotypingMethod = getClonotypeEnumFromDefinition(clonotypeDefinition) as ClonotypingMethod;

  const [startClonotypeAnalysis, { isLoading, isError }] = useStartClonotypeAnalysis();

  const handleStartClonotypeAnalysis = React.useCallback(async () => {
    const result = await startClonotypeAnalysis({
      experimentId: experimentId!,
      clonotypeId,
      barcodes,
      clonotypeMethod: clonotypingMethod,
    });
    if (result && 'data' in result) {
      setData(result.data);
    }
  }, [startClonotypeAnalysis, experimentId, clonotypeId, barcodes, clonotypingMethod]);

  const mounted = React.useRef(false);

  React.useEffect(() => {
    if (mounted.current) {
      // needed to prevent strict mode from running the effect twice in
      return;
    }
    mounted.current = true;
    (async () => {
      await handleStartClonotypeAnalysis();
    })();
    //  The effect should only run once when the component mounts
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isError) {
    return (
      <Modal opened onClose={onClose} title="Multi-alignments" size="lg">
        <Stack gap={5} h={250} justify="center" align="center">
          <FontAwesomeIcon icon={faExclamationCircle} size="2x" color="red" />
          <Text c="red.7">Failed to load data, please try again</Text>
        </Stack>
      </Modal>
    );
  }

  if (isLoading || !data) {
    return (
      <Modal data-testid="clonotype-analysis-loading" opened onClose={onClose} size="lg">
        <LoadingContainer height={300} text="Calculating multi-alignments..." />
      </Modal>
    );
  }

  return (
    <Modal
      data-testid="clonotype-analysis"
      opened
      onClose={onClose}
      title={
        <Title order={5} data-testid={`clonotype-analysis-modal-${clonotypeId}`}>
          Multi-alignments of clonotype {clonotypeId}
        </Title>
      }
      size="auto"
    >
      <Group>
        <Button
          loading={isLoading}
          variant="outline"
          size="compact-sm"
          mt="xs"
          onClick={() =>
            downloadFile({
              experimentId: experimentId!,
              setIsLoading: setIsLoadingVhAaDownload,
              filePath: data.heavyData?.inFileName,
            })
          }
        >
          Download VH AA FASTA
        </Button>
        <Button
          loading={isLoading}
          variant="outline"
          size="compact-sm"
          mt="xs"
          onClick={() =>
            downloadFile({
              experimentId: experimentId!,
              setIsLoading: setIsLoadingVlAaDownload,
              filePath: data.lightData?.inFileName,
            })
          }
        >
          Download VL AA FASTA
        </Button>
        <Button
          loading={isLoading}
          variant="outline"
          size="compact-sm"
          mt="xs"
          onClick={() =>
            downloadFile({
              experimentId: experimentId!,
              setIsLoading: setIsLoadingVhDnaDownload,
              filePath: data.heavyData?.dnaFileName,
            })
          }
        >
          Download VH DNA FASTA
        </Button>
        <Button
          loading={isLoading}
          variant="outline"
          size="compact-sm"
          mt="xs"
          onClick={() =>
            downloadFile({
              experimentId: experimentId!,
              setIsLoading: setIsLoadingVlDnaDownload,
              filePath: data.lightData?.dnaFileName,
            })
          }
        >
          Download VL DNA FASTA
        </Button>
      </Group>
      <Space mb="xl" />
      <ScrollArea scrollbars="x" pb={30}>
        <Stack gap={50}>
          <Stack gap={5} data-testid="clonotype-analysis-heavy-chains">
            <Text size="md" fw={500} display="inline">
              Multi-alignments of heavy chains [
              <Text size="md" fw={500} c="blue.3" component="span" mr={5}>
                CDR1
              </Text>
              <Text size="md" fw={500} c="blue.5" component="span" mr={5}>
                CDR2
              </Text>
              <Text size="md" fw={500} c="blue.7" component="span">
                CDR3
              </Text>
              ]
            </Text>
            <ScrollArea scrollbars="x" pb={30}>
              {Object.keys(data.heavyData.alignedData).map((key) => {
                const row = data.heavyData.alignedData[key];
                const coloredIndexes = data.heavyData.coloredIndexes[key];
                if (!row || !coloredIndexes) {
                  return null;
                }

                return (
                  <Flex key={key}>
                    <Text size="sm" mr="xs" ff="monospace" style={{ whiteSpace: 'nowrap' }}>
                      {key}
                    </Text>
                    <HighlighText text={row} coloredIndexes={coloredIndexes} />
                  </Flex>
                );
              })}
            </ScrollArea>
          </Stack>

          {data.lightData && (
            <Stack miw={1050} gap={5} data-testid="clonotype-analysis-light-chains">
              <Text size="md" fw={500} display="inline">
                Multi-alignments of light chains [
                <Text size="md" fw={500} c="blue.3" component="span" mr={5}>
                  CDR1
                </Text>
                <Text size="md" fw={500} c="blue.5" component="span" mr={5}>
                  CDR2
                </Text>
                <Text size="md" fw={500} c="blue.7" component="span">
                  CDR3
                </Text>
                ]
              </Text>
              <ScrollArea scrollbars="x" pb={30}>
                {Object.keys(data.lightData.alignedData).map((key) => {
                  const row = data.lightData?.alignedData[key];
                  const coloredIndexes = data.lightData?.coloredIndexes[key];
                  if (!row || !coloredIndexes) {
                    return null;
                  }

                  return (
                    <Flex key={key}>
                      <Text size="sm" mr="xs" ff="monospace" style={{ whiteSpace: 'nowrap' }}>
                        {key}
                      </Text>
                      <HighlighText text={row} coloredIndexes={coloredIndexes} />
                    </Flex>
                  );
                })}
              </ScrollArea>
            </Stack>
          )}

          <SimpleGrid cols={1}>
            {data.tanglegramImage && (
              <Stack data-testid="tanglegram">
                <Text size="md" fw={500}>
                  VH/VL linked phylogram (tanglegram)
                </Text>
                <div
                  style={{
                    objectFit: 'contain',
                  }}
                >
                  <img src={`data:image/png;base64,${data.tanglegramImage}`} width={400} alt="VH/VL linked phylogram (tanglegram)" />
                </div>
              </Stack>
            )}
          </SimpleGrid>
        </Stack>
      </ScrollArea>
    </Modal>
  );
}
